import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, CompetitionPoster, PosterImage } from '@structural-climbing/ui'
import type { CompetitionDetail } from '../types'
import { entryModeOf } from './useCompetitionForm'
import { missingForPublish, type SetupStep } from './steps'
import { stepLabel } from './stepLabel'
import { StepPanel } from './StepPanel'

/** "15 nov 2026, 10:00" from a gym-local wall clock, never shifted by the viewer's zone. */
function wallClock(value: string, locale: string): string {
  const [date, time = '00:00'] = value.split('T')
  const [y, m, d] = date.split('-').map(Number)
  const [h, mi] = time.split(':').map(Number)
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(y, m - 1, d, h, mi))
}

/**
 * Step 7: everything on one screen, each line a way back to its step, then
 * Publish. Publishing is what opens Running and After and lets members enter.
 */
export function PublishStep({
  competition,
  onGoto,
  onBack,
  onChanged,
}: {
  competition: CompetitionDetail
  onGoto: (step: SetupStep) => void
  onBack: () => void
  onChanged: () => Promise<void> | void
}) {
  const { t, i18n } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const c = competition
  const locale = i18n.language.startsWith('ro') ? 'ro-RO' : 'en-GB'
  const missing = missingForPublish(c)
  const single = c.format === 'single_day'
  const slots = single ? c.sessions : c.rounds

  async function setPublished(published: boolean) {
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/competitions/${c.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ published }),
      })
      if (!res.ok) throw new Error()
      await onChanged()
    } catch {
      setError(t('competitions.errorSaveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const fee =
    c.entry_fee_cents != null && c.entry_fee_currency
      ? ` · ${(c.entry_fee_cents / 100).toLocaleString(locale)} ${c.entry_fee_currency}`
      : ''
  const rows: { key: SetupStep; label: string; value: string }[] = [
    {
      key: 'when',
      label: t('competitionSetup.summaryWhen'),
      value: `${wallClock(c.starts_at, locale)} – ${wallClock(c.ends_at, locale)} · ${t(`competitions.format_${c.format}`)}`,
    },
    {
      key: 'when',
      label: t('competitionSetup.summaryDiscipline'),
      value: `${t(`competitions.discipline_${c.discipline}`)} · ${t(`competitions.scoring_${c.scoring_type}`)}`,
    },
    {
      key: 'entry',
      label: t('competitionSetup.summaryEntry'),
      value: `${t(`competitions.joinPolicy_${c.join_policy}`)} · ${t(`competitionSetup.mode_${entryModeOf(c)}`)}${fee}`,
    },
    {
      key: 'categories',
      label: t('competitionSetup.summaryCategories'),
      value: c.categories.map((x) => x.name).join(', ') || t('competitionSetup.summaryNone'),
    },
    {
      key: 'slots',
      label: single ? t('competitionSetup.summaryGroups') : t('competitionSetup.summaryRounds'),
      value: slots.map((x) => x.name).join(', ') || t('competitionSetup.summaryNone'),
    },
    {
      key: 'routes',
      label: t('competitionSetup.summaryRoutes'),
      value: c.routes.length
        ? t('competitions.routeCount', { count: c.routes.length })
        : t('competitionSetup.summaryNone'),
    },
  ]

  return (
    <StepPanel
      onBack={onBack}
      error={error}
      primary={
        c.published
          ? {
              label: t('competitionSetup.unpublish'),
              onPress: () => void setPublished(false),
              isLoading: saving,
            }
          : {
              label: t('competitionSetup.publishCta'),
              onPress: () => void setPublished(true),
              isLoading: saving,
              isDisabled: missing.length > 0,
            }
      }
    >
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 className="mb-3 text-xl font-bold">{t('competitionSetup.publishTitle')}</h2>
          <dl className="flex flex-col">
            {rows.map((row) => (
              <div
                key={row.label}
                className="flex flex-col gap-1 border-t border-divider py-3 text-sm sm:flex-row sm:items-center sm:gap-4"
              >
                <dt className="w-32 shrink-0 text-foreground/60">{row.label}</dt>
                <dd className="min-w-0 flex-1 break-words">{row.value}</dd>
                <Button size="sm" variant="light" color="primary" onPress={() => onGoto(row.key)}>
                  {t('competitionSetup.change')}
                </Button>
              </div>
            ))}
          </dl>
          <p
            className={`mt-3 rounded-xl px-4 py-3 text-sm ${
              c.published || missing.length === 0
                ? 'bg-success-50 text-success-800'
                : 'bg-warning-50 text-warning-800'
            }`}
          >
            {c.published
              ? t('competitionSetup.publishedNote')
              : missing.length === 0
                ? t('competitionSetup.publishReady')
                : t('competitionSetup.publishMissing', {
                    steps: missing.map((s) => stepLabel(s, c.format, t)).join(', '),
                  })}
          </p>
        </div>

        <aside
          className="flex w-full flex-col gap-2 lg:w-64"
          aria-label={t('competitionSetup.previewTitle')}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground/60">
            {t('competitionSetup.previewTitle')}
          </p>
          <div className="overflow-hidden rounded-2xl border border-divider bg-content2">
            <div className="aspect-[4/3] w-full">
              {c.poster_url ? (
                <PosterImage src={c.poster_url} />
              ) : (
                <CompetitionPoster name={c.name} />
              )}
            </div>
            <div className="flex flex-col gap-1 p-3">
              <p className="font-semibold break-words">{c.name}</p>
              <p className="text-xs text-foreground/60">{wallClock(c.starts_at, locale)}</p>
            </div>
          </div>
        </aside>
      </div>
    </StepPanel>
  )
}
